/**
 * Loads a gateway's own JS SDK, asynchronously, with a functional fallback if it never arrives
 * (WP-18: "never blocks on a slow third-party script — gateway scripts async with fallback",
 * PRD §1.5.5).
 *
 * ## Subresource Integrity — why `GATEWAYS` has no hash today
 *
 * `10-SECURITY-HARDENING.md` §10.8 requires "Subresource Integrity on every external script that
 * supports it," and WP-18's own requirement repeats it: "this is the package that loads them, so
 * it is the package that pins them." Both Stripe.js v3 and the PayPal JS SDK are documented by
 * their own vendors as **not supporting SRI**: both are served from a URL with no version in the
 * path and are updated by the vendor, silently, on a cadence neither vendor commits to — Stripe's
 * own integration guide states this explicitly, and PayPal's SDK loader is the same shape.
 * Pinning a hash today would not add integrity checking; it would turn the vendor's next routine
 * update into a broken checkout, the first time the hash stopped matching, for every donor on
 * this site until someone noticed and shipped a new one.
 *
 * So `integrity` here is a config value this loader is *built* to attach, left empty until a
 * version exists that can be hashed without producing exactly that outage. `docs/build-log/
 * WP-18-checkout-ui-standard-lane.md`'s "Where the spec was wrong, unclear, or incomplete"
 * records this rather than silencing the gap with a hash that helps nobody.
 */

/**
 * @type {Record<string, {src: string, globalName: string, integrity: string}>}
 */
const GATEWAYS = {
	stripe: { src: 'https://js.stripe.com/v3/', globalName: 'Stripe', integrity: '' },
	paypal: { src: 'https://www.paypal.com/sdk/js', globalName: 'paypal', integrity: '' },
};

const loaded = new Map();

/**
 * Loads a gateway's script once, resolving with its global once ready, or with `null` after
 * `timeoutMs` if it never loads — a blocked request, an ad blocker, or a network failure must
 * degrade the payment step to a stated fallback, never to a page that hangs silently.
 *
 * @param {string} gateway
 * @param {number} [timeoutMs]
 * @return {Promise<any|null>}
 */
export function loadGateway( gateway, timeoutMs = 8000, publicConfig = {} ) {
	const config = GATEWAYS[ gateway ];

	if ( ! config ) {
		return Promise.resolve( null );
	}

	/*
	 * PayPal's SDK will not load at all without its client id, and loads the wrong currency
	 * and intent without the other two (release gate 0.5.3, adversarial review round 4). The
	 * values come from the intent response's own `public_config`, never from the page.
	 */
	let src = config.src;

	if ( 'paypal' === gateway ) {
		if ( ! publicConfig.client_id ) {
			return Promise.resolve( null );
		}

		// A recurring gift is a subscription the donor approves; PayPal's SDK needs to be told so at load.
		const parameters = {
			'client-id': publicConfig.client_id,
			currency: publicConfig.currency || 'USD',
			intent: publicConfig.recurring ? 'subscription' : 'capture',
			components: 'buttons',
			// B2 (rc2): one PayPal button. The card form covers cards; a second card option confuses donors.
			'disable-funding': 'card,credit,paylater,venmo',
		};

		if ( publicConfig.recurring ) {
			parameters.vault = 'true';
		}

		src += '?' + new URLSearchParams( parameters ).toString();
	}

	if ( loaded.has( src ) ) {
		return loaded.get( src );
	}

	const promise = new Promise( ( resolve ) => {
		let settled = false;

		const timer = window.setTimeout( () => {
			if ( ! settled ) {
				settled = true;
				resolve( null );
			}
		}, timeoutMs );

		const script = document.createElement( 'script' );

		script.src = src;
		script.async = true;

		// CORS mode only where an integrity hash needs it: without one it adds nothing, and a
		// provider that sends no CORS header would never load (adversarial review round 5, LOW-3).
		if ( '' !== config.integrity ) {
			script.crossOrigin = 'anonymous';
			script.integrity = config.integrity;
		}

		script.addEventListener( 'load', () => {
			window.clearTimeout( timer );

			if ( ! settled ) {
				settled = true;
				resolve( window[ config.globalName ] || null );
			}
		} );

		script.addEventListener( 'error', () => {
			window.clearTimeout( timer );

			if ( ! settled ) {
				settled = true;
				resolve( null );
			}
		} );

		document.head.appendChild( script );
	} );

	loaded.set( src, promise );

	// A load that failed or timed out is not remembered: the next attempt tries again rather
	// than being handed the same null until the page is reloaded (round 5, LOW-3).
	promise.then( ( global ) => {
		if ( ! global ) {
			loaded.delete( src );
		}
	} );

	return promise;
}
